import type { Ref, ShallowRef } from "vue";
import { customRef, reactive, ref, shallowRef } from "vue";

/**
 * Creates a ref but casts away any unwrapping vue might actually be doing.
 * Note. This will cause hard to track issues if you are wrapping objects containing nested refs.
 *
 * TODO: Try and prevent T containing nested Refs with type constraints.
 *
 * If you only want to react to reassignment of a ref then consider shallowRef();
 */
export function makeDeepRef<T>(raw: T): Ref<T> {
  return ref(raw) as Ref<T>;
}

export function makeRef<T>(raw: T): ShallowRef<T> {
  return shallowRef(raw) as ShallowRef<T>;
}

export function makeReactive<T extends object>(raw: T): T {
  return reactive(raw) as T;
}

/**
 * Create a shallow reference to an object.
 * - Prevents mutation of the object (single level).
 * - Typed to prevent mutation of nested objects/values.
 * - Mutation of item at any depth will not trigger reactivity.
 *
 * Newly assigned objects follow the same rules (not just the initial value)
 *
 * @deprecated Use makeRef<T>() instead
 */
export function shallowReadonlyObjRef<T extends object | null>(
  raw: T
): Ref<Readonly<T>>;
/**
 * @deprecated Use makeRef<T>() instead
 */
export function shallowReadonlyObjRef<T extends object>(): Ref<
  Readonly<T | undefined>
>;
/**
 * @deprecated Use makeRef<T>() instead
 */
export function shallowReadonlyObjRef(raw?: object) {
  // Note. We could implement a deep freeze here - but unsure of perf cost (and more risky to roll out).
  // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/freeze for a reference
  // example of deep freeze.
  const freezeFunc = (item?: object) => Object.freeze(item);

  // Use customRef so we can re-apply freeze on re-assigned values.
  // https://vuejs.org/api/reactivity-advanced.html#customref
  return customRef<Readonly<object | undefined>>((track, trigger) => {
    let value = freezeFunc(raw);
    return {
      get() {
        track();
        return value;
      },
      set(newValue) {
        value = freezeFunc(newValue);
        trigger();
      },
    };
  });
}

export type ReadonlyEntityArray<T> = readonly Readonly<T>[];

/**
 * Converts each property of an object to a Ref
 */
export type ConvertToRefs<T extends object> = {
  [K in keyof T]: Ref<T[K]>;
};
